/*
    auth.ts

    We are using the MSAL authentication library with Azure.

    MSAL writes to session storage using a key similar to below:
    a7f97eac-67d5-4c9b-a8a2-88a2f0b7dcef.9f0a46bc-af1b-40f2-ae2c-5d82bfb4c41a-login.windows.net-0ec4d3be-d7e7-4167-8cb9-688ce1f08d51

    Main objects:

        msalClientApp                   // the PublicClientApplication returned after login
        msalAuthStore                   // Svelte store for components
        sessionAccount                  // MS supplied
        storeAccount                    // copy of sessionUser


    TODO:   add selectAccount()
            handle logout better

            loginRedirect
            handleRedirectPromise       // must implement this
            acquireTokenRedirect
            acquireTokenSilent          // use as Bearer token

            getAllAccounts
            getAccountByUsername
            getAccountByHomeId


    // sessionUser

    authorityType: "MSSTS"
    clientInfo: "eyJ1aWQiOiJhN2Y5N2VhY..."
    environment: "login.windows.net"
    homeAccountId: "a7f97eac-67d5-4c9b-a8a2-88a2f0b7dcef.9f0a46bc-af1b-40f2-ae2c-5..."
    idTokenClaims:
        aud: "d7dc1c9d-7b7f-4f4e-b78a-fa19..."
        exp: 1654108957
        iat: 1654105057
        idp: "https://sts.windows.net/9f0a46bc-af1b-40f2-ae2c-5d82.../"
        iss: "https://login.microsoftonline.com/0ec4d3be-d7e7-4167-8cb9-688c.../v2.0"
        name: "Andy Knoll"
        nbf: 1654105057
        nonce: "443e9ebf-1fab-406b-89f7-387967..."
        oid: "9c1f26d6-9185-4b31-98bd-476438..."
        preferred_username: "andy.knoll@tcare.ai"
        rh: "0.AR0AvtPEDufXZ0GMuWiM4fCNUZ0c3Nd_e05Pt4r6GcCE0..."
        roles: ['search']
        sub: "JDa5u1qAriFTkzU20WawyubieufAnC0VM7ja8..."
        tid: "0ec4d3be-d7e7-4167-8cb9-688ce1..."
        uti: "7Xhz2UlFXkeUeC3S..."
        ver: "2.0"
    localAccountId: "9c1f26d6-9185-4b31-98bd-476438..."
    name: "Andy Knoll"
    realm: "0ec4d3be-d7e7-4167-8cb9-688ce1..."
    username: "andy.knoll@tcare.ai"

*/
var _a;
import { getMSALConfig } from '../../msal.config.js'; // settings for MSAL app instance
import * as msal from "@azure/msal-browser"; // need this if using npm package
import { writable, get } from 'svelte/store';
import { AccountStore, clearUser, setAccountFromMSALResponse } from "./account";
// Create the stores
export const storeAccount = writable(null); // Svelte store for reactive components
// Create the main MSAL app instance
const msalClientApp = new msal.PublicClientApplication(getMSALConfig());
import getBindings from '../../config.js';
// import {json} from "stream/consumers";
// MSAL object props
// let username;                                               // only used by logoutRequest
// let sessionAccount = null;                                  // MS supplied
const bindings = getBindings();
// MSAL functions
// *REQUIRED* 
// A promise handler needs to be registered for handling the response returned from redirect flow
msalClientApp.handleRedirectPromise()
    .then(response => {
    // console.log("handling redirect promise")
    // console.log(JSON.stringify(response))
    if (response) {
        if (response.idTokenClaims['tfp'].toUpperCase() === bindings.b2cPolicies.names.signUpSignIn.toUpperCase()) {
            handleResponse(response);
        }
    }
})
    .catch((error) => {
    console.error(error);
});
export const persistAccount = () => {
    selectAccount(); //todo check if necessary
};
// NOTE: this returns an MSAL account (not user)
// To see the full list of response object properties, visit:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
function handleResponse(response) {
    //todo merge feature/azb2c into this branch
    if (response !== null) {
        // console.log("response not nil, setting account")
        setAccountFromMSALResponse(response.account);
    }
    else {
        selectAccount();
    }
}
// You can pass a custom request object below. This will override the initial configuration. For more information, visit:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
export const signIn = () => {
    // todo
    // pull in correct scopes needed as part of login
    // write account to store (handled in part of login response listener)
    //
    // console.log('signIn');
    try {
        // msalClientApp.loginRedirect(loginRequest);
        msalClientApp.loginRedirect({
            scopes: ["openid"] //todo, other scopes needed? -vjm
        }); // this works too
    }
    catch (err) {
        console.error(err);
    }
};
// You can pass a custom request object below. This will override the initial configuration. For more information, visit:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
export const signOut = () => {
    clearUser();
    msalClientApp.logoutRedirect(logoutRequest); // must call this!
};
// Choose which account to logout from by passing a username.
const logoutRequest = {
    account: msalClientApp.getAccountByUsername((_a = get(AccountStore)) === null || _a === void 0 ? void 0 : _a.username),
    postLogoutRedirectUri: getMSALConfig().auth.redirectUri,
};
// export const getSessionAccount = (): Account => { //todo is necessary? maybe just to load select account stack -vjm
//     return get(AccountStore)
// }
// various account props
export const getSessionLocalAccountId = () => {
    var _a, _b;
    return (_b = (_a = get(AccountStore)) === null || _a === void 0 ? void 0 : _a.accountId) !== null && _b !== void 0 ? _b : null;
};
export const getSessionUsername = () => {
    var _a, _b;
    return (_b = (_a = get(AccountStore)) === null || _a === void 0 ? void 0 : _a.username) !== null && _b !== void 0 ? _b : null;
};
export const getSessionRoles = () => {
    var _a, _b;
    return (_b = (_a = get(AccountStore)) === null || _a === void 0 ? void 0 : _a.roles) !== null && _b !== void 0 ? _b : [];
};
export const hasSessionRoles = (rolesArr) => {
    let roles = getSessionRoles();
    if (!roles || roles.length === 0 || rolesArr.length === 0)
        return false;
    for (const role of rolesArr) {
        if (!roles.includes(role))
            return false;
    }
    return true;
};
function selectAccount() {
    // console.log("attempting select account");
    /**
     * See here for more information on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    const currentAccounts = msalClientApp.getAllAccounts();
    if (currentAccounts.length < 1) {
        return;
    }
    else if (currentAccounts.length > 1) {
        /**
         * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
         * is cached as a new account, which results in more than one account in the cache. Here we make
         * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
         * as this is the default flow the user initially signed-in with.
         */
        const accounts = currentAccounts.filter(account => account.homeAccountId.toUpperCase().includes(bindings.b2cPolicies.names.signUpSignIn.toUpperCase())
            &&
                account.idTokenClaims.iss.toUpperCase().includes(bindings.b2cPolicies.authorityDomain.toUpperCase())
            &&
                account.idTokenClaims.aud === getMSALConfig().auth.clientId);
        if (accounts.length > 1) {
            // localAccountId identifies the entity for which the token asserts information.
            if (accounts.every(account => account.localAccountId === accounts[0].localAccountId)) {
                // All accounts belong to the same user
                setAccountFromMSALResponse(accounts[0]);
            }
            else {
                // Multiple users detected. Logout all to be safe.
                signOut();
            }
            ;
        }
        else if (accounts.length === 1) {
            setAccountFromMSALResponse(accounts[0]);
        }
    }
    else if (currentAccounts.length === 1) {
        setAccountFromMSALResponse(currentAccounts[0]);
    }
}
// in case of page refresh
selectAccount();
// Vito code
export function getTokenRedirect(request) {
    let loggedIn = get(AccountStore).isLoggedIn;
    // console.log("attempting token redirect, logged in = ",loggedIn);
    if (!loggedIn) {
        return;
    }
    /**
     * See here for more info on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    let act = msalClientApp.getAccountByHomeId(get(AccountStore).accountId);
    request.account = act;
    // console.log("attempting acquire token silent", JSON.stringify(act));
    // console.log(JSON.stringify(request))
    /**
     *
     */
    return msalClientApp.acquireTokenSilent(request)
        .then((response) => {
        // In case the response from B2C server has an empty accessToken field
        // throw an error to initiate token acquisition
        // console.log("got response",JSON.stringify(response))
        if (!response.accessToken || response.accessToken === "") {
            throw new msal.InteractionRequiredAuthError;
        }
        else {
            // console.log("access_token acquired at: " + new Date().toString());
            // accessToken = response.accessToken;
            // passTokenToApi();
            return response.accessToken;
        }
    }).catch(error => {
        console.log(error); //keep
        signOut();
        //todo evaluate correct action if cannot find token, right now that means you shouldn't be logged in
        // //todo undo
        // console.log("Silent token acquisition fails. Acquiring token using redirect. \n", error);
        // if (error instanceof msal.InteractionRequiredAuthError) {
        //     // fallback to interaction when silent call fails
        //     return msalClientApp.acquireTokenRedirect(request);
        // } else {
        // }
    });
}
